import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/components/AuthProvider";
import { ThemeProvider } from "@/components/ThemeProvider";
import { LanguageProvider } from "@/components/LanguageContext";
import { useAnalytics } from "@/hooks/useAnalytics";
import { useMetaPixel } from "@/hooks/useMetaPixel";
import { CookieConsent } from "@/components/shared/CookieConsent";
import { SEOHead } from "@/components/SEOHead";
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import StudentAuth from "./pages/StudentAuth";
import Dashboard from "./pages/Dashboard";
import StudentDashboard from "./pages/StudentDashboard";
import NotFound from "./pages/NotFound";
import AdminPage from "./pages/AdminPage";
import { ProgressList } from "./components/progress/ProgressList";
import { StudentProgress } from "./components/progress/StudentProgress";
import { PublicProfile } from "./pages/PublicProfile";
import Plan from "@/pages/Plan";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import ResetPassword from "./pages/ResetPassword";
import { useEffect } from "react";
import ScrollToTop from "./components/ScrollToTop";
import { StudentsList } from "./components/students/StudentsList";
import NewStudent from "./pages/NewStudent";
import EditStudent from "./pages/EditStudent";
import { ExerciseLibraryPage } from "./pages/ExerciseLibraryPage";
import { MessagesPage } from "./components/messaging/MessagesPage";
import FeaturesPage from "./pages/FeaturesPage";
import BenefitsPage from "./pages/BenefitsPage";
import PricingPage from "./pages/PricingPage";
import TestimonialsPage from "./pages/TestimonialsPage";
import CompleteProfile from "./pages/CompleteProfile";

const PORTUGUESE_COUNTRIES = [
  "PT", // Portugal
  "BR", // Brazil
  "AO", // Angola
  "MZ", // Mozambique
  "GW", // Guinea-Bissau
  "CV", // Cape Verde
  "ST", // São Tomé and Príncipe
  "TL", // East Timor
  "GQ", // Equatorial Guinea
];

const SPANISH_COUNTRIES = [
  "ES", // Spain
  "MX", // Mexico
  "CO", // Colombia
  "AR", // Argentina
  "PE", // Peru
  "VE", // Venezuela
  "CL", // Chile
  "EC", // Ecuador
  "GT", // Guatemala
  "CU", // Cuba
  "BO", // Bolivia
  "DO", // Dominican Republic
  "HN", // Honduras
  "PY", // Paraguay
  "SV", // El Salvador
  "NI", // Nicaragua
  "CR", // Costa Rica
  "PA", // Panama
  "UY", // Uruguay
  "PR", // Puerto Rico
  "GQ", // Equatorial Guinea
];

const FRENCH_COUNTRIES = [
  "FR", // France
  "CA", // Canada (partly)
  "BE", // Belgium
  "CH", // Switzerland (partly)
  "LU", // Luxembourg
  "MC", // Monaco
  "HT", // Haiti
  "CI", // Côte d'Ivoire
  "CD", // DR Congo
  "MG", // Madagascar
  "CM", // Cameroon
  "SN", // Senegal
  "ML", // Mali
  "NE", // Niger
  "BJ", // Benin
  "GA", // Gabon
  "TG", // Togo
  "CF", // Central African Republic
  "CG", // Republic of the Congo
  "DJ", // Djibouti
];

const LanguageRoot = () => {
  useEffect(() => {
    const savedLanguage = localStorage.getItem('language');
    if (savedLanguage === 'pt') {
      window.location.pathname = '/pt';
      return;
    } else if (savedLanguage === 'es') {
      window.location.pathname = '/es';
      return;
    } else if (savedLanguage === 'fr') {
      window.location.pathname = '/fr';
      return;
    } else if (savedLanguage === 'en') {
      return;
    }
    
    try {
      const browserLanguage = navigator.language.split('-')[1];
      if (browserLanguage) {
        const country = browserLanguage.toUpperCase();
        if (PORTUGUESE_COUNTRIES.includes(country)) {
          window.location.pathname = '/pt';
          return;
        } else if (SPANISH_COUNTRIES.includes(country)) {
          window.location.pathname = '/es';
          return;
        } else if (FRENCH_COUNTRIES.includes(country)) {
          window.location.pathname = '/fr';
          return;
        }
      }
      
      // Check language code directly if region detection fails
      const languageCode = navigator.language.split('-')[0].toLowerCase();
      if (languageCode === 'pt') {
        window.location.pathname = '/pt';
        return;
      } else if (languageCode === 'es') {
        window.location.pathname = '/es';
        return;
      } else if (languageCode === 'fr') {
        window.location.pathname = '/fr';
        return;
      }
      
      if ('Intl' in window && Intl.Locale) {
        const locale = new Intl.Locale(navigator.language);
        if (locale.region) {
          const country = locale.region;
          if (PORTUGUESE_COUNTRIES.includes(country)) {
            window.location.pathname = '/pt';
            return;
          } else if (SPANISH_COUNTRIES.includes(country)) {
            window.location.pathname = '/es';
            return;
          } else if (FRENCH_COUNTRIES.includes(country)) {
            window.location.pathname = '/fr';
            return;
          }
        }
      }
    } catch (e) {
      console.error("Error detecting language:", e);
    }
  }, []);
  
  return <Index />;
};

const App = () => {
  useAnalytics();
  useMetaPixel();
  
  return (
    <ThemeProvider defaultTheme="light" storageKey="trainai-theme">
      <AuthProvider>
        <TooltipProvider delayDuration={0}>
          <div className="min-h-screen bg-background">
            <SEOHead 
              title="TrainerFOLIO" 
              description="Plataforma para Personal Trainers e seus Alunos"
            />
            <ScrollToTop />
            <Sonner />
            <Routes>
              <Route path="/" element={<LanguageRoot />} />
              
              {/* Admin Routes */}
              <Route path="/adm/*" element={<AdminPage />} />
              
              <Route path="/features" element={<FeaturesPage />} />
              <Route path="/complete-profile" element={<CompleteProfile />} />
              <Route path="/benefits" element={<BenefitsPage />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/testimonials" element={<TestimonialsPage />} />
              
              {/* Portuguese Routes */}
              <Route path="/pt" element={<Index />} />
              <Route path="/pt/features" element={<FeaturesPage />} />
              <Route path="/pt/benefits" element={<BenefitsPage />} />
              <Route path="/pt/pricing" element={<PricingPage />} />
              <Route path="/pt/testimonials" element={<TestimonialsPage />} />
              
              <Route path="/pt/auth" element={<Auth />} />
              <Route path="/pt/complete-profile" element={<CompleteProfile />} />
              <Route path="/pt/student-auth" element={<StudentAuth />} />
              <Route path="/pt/terms" element={<Terms />} />
              <Route path="/pt/privacy" element={<Privacy />} />
              <Route path="/pt/pp/:publicUrl" element={<PublicProfile />} />
              <Route path="/pt/reset-password" element={<ResetPassword />} />
              
              <Route path="/pt/dashboard/*" element={<Dashboard />}>
                <Route path="progress" element={<ProgressList />} />
                <Route path="progress/:id" element={<StudentProgress />} />
                <Route path="plan" element={<Plan />} />
                <Route path="students" element={<StudentsList />} />
                <Route path="students/new" element={<NewStudent />} />
                <Route path="students/edit/:id" element={<EditStudent />} />
                <Route path="exercise-library" element={<ExerciseLibraryPage />} />
                <Route path="messages" element={<MessagesPage />} />
              </Route>
              
              <Route path="/pt/student-dashboard/*" element={<StudentDashboard />} />
              
              {/* Spanish Routes */}
              <Route path="/es" element={<Index />} />
              <Route path="/es/features" element={<FeaturesPage />} />
              <Route path="/es/benefits" element={<BenefitsPage />} />
              <Route path="/es/pricing" element={<PricingPage />} />
              <Route path="/es/testimonials" element={<TestimonialsPage />} />
              
              <Route path="/es/auth" element={<Auth />} />
              <Route path="/es/complete-profile" element={<CompleteProfile />} />
              <Route path="/es/student-auth" element={<StudentAuth />} />
              <Route path="/es/terms" element={<Terms />} />
              <Route path="/es/privacy" element={<Privacy />} />
              <Route path="/es/pp/:publicUrl" element={<PublicProfile />} />
              <Route path="/es/reset-password" element={<ResetPassword />} />
              
              <Route path="/es/dashboard/*" element={<Dashboard />}>
                <Route path="progress" element={<ProgressList />} />
                <Route path="progress/:id" element={<StudentProgress />} />
                <Route path="plan" element={<Plan />} />
                <Route path="students" element={<StudentsList />} />
                <Route path="students/new" element={<NewStudent />} />
                <Route path="students/edit/:id" element={<EditStudent />} />
                <Route path="exercise-library" element={<ExerciseLibraryPage />} />
                <Route path="messages" element={<MessagesPage />} />
              </Route>
              
              <Route path="/es/student-dashboard/*" element={<StudentDashboard />} />
              
              {/* French Routes */}
              <Route path="/fr" element={<Index />} />
              <Route path="/fr/features" element={<FeaturesPage />} />
              <Route path="/fr/benefits" element={<BenefitsPage />} />
              <Route path="/fr/pricing" element={<PricingPage />} />
              <Route path="/fr/testimonials" element={<TestimonialsPage />} />
              
              <Route path="/fr/auth" element={<Auth />} />
              <Route path="/fr/complete-profile" element={<CompleteProfile />} />
              <Route path="/fr/student-auth" element={<StudentAuth />} />
              <Route path="/fr/terms" element={<Terms />} />
              <Route path="/fr/privacy" element={<Privacy />} />
              <Route path="/fr/pp/:publicUrl" element={<PublicProfile />} />
              <Route path="/fr/reset-password" element={<ResetPassword />} />
              
              <Route path="/fr/dashboard/*" element={<Dashboard />}>
                <Route path="progress" element={<ProgressList />} />
                <Route path="progress/:id" element={<StudentProgress />} />
                <Route path="plan" element={<Plan />} />
                <Route path="students" element={<StudentsList />} />
                <Route path="students/new" element={<NewStudent />} />
                <Route path="students/edit/:id" element={<EditStudent />} />
                <Route path="exercise-library" element={<ExerciseLibraryPage />} />
                <Route path="messages" element={<MessagesPage />} />
              </Route>
              
              <Route path="/fr/student-dashboard/*" element={<StudentDashboard />} />
              
              {/* English Routes (Default) */}
              <Route path="/auth" element={<Auth />} />
              <Route path="/student-auth" element={<StudentAuth />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              
              <Route path="/dashboard/*" element={<Dashboard />}>
                <Route path="progress" element={<ProgressList />} />
                <Route path="progress/:id" element={<StudentProgress />} />
                <Route path="plan" element={<Plan />} />
                <Route path="students" element={<StudentsList />} />
                <Route path="students/new" element={<NewStudent />} />
                <Route path="students/edit/:id" element={<EditStudent />} />
                <Route path="exercise-library" element={<ExerciseLibraryPage />} />
                <Route path="messages" element={<MessagesPage />} />
              </Route>
              
              <Route path="/student-dashboard/*" element={<StudentDashboard />} />
              <Route path="/pp/:publicUrl" element={<PublicProfile />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy" element={<Privacy />} />
              
              {/* Catch-all route for 404s */}
              <Route path="*" element={<NotFound />} />
            </Routes>
            <CookieConsent />
          </div>
        </TooltipProvider>
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;
